import React, { useState, useCallback } from 'react';
import { FaSpinner } from 'react-icons/fa';
import { fetchToken } from '../../../lib/globalApiClient';

const BACKEND_API = process.env.REACT_APP_APIUrl || 'https://api.importerr.com/api/';

const SearchBySkuUrl = ({
  onSearchComplete,
  onSearchStart,
  fetchMinVariantPrice,
  disabled = false
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searching, setSearching] = useState(false);

  // Extract offerId from 1688 URL
  const extractOfferIdFromUrl = useCallback((url) => {
    try {
      const urlObj = new URL(url);
      const params = new URLSearchParams(urlObj.search);
      const paramOfferId = params.get('offer') || params.get('offerId');
      if (paramOfferId) return paramOfferId;

      const pathMatch = urlObj.pathname.match(/\/offer\/([0-9]+)/);
      return pathMatch ? pathMatch[1] : null;
    } catch {
      const offerMatch = url.match(/\/offer\/([0-9]+)|offer[=:]([0-9]+)/);
      return offerMatch ? (offerMatch[1] || offerMatch[2]) : null;
    }
  }, []);

  // Search by SKU or 1688 URL
  const handleSearch = useCallback(async () => {
    const trimmed = (searchTerm || '').trim();
    if (!trimmed) {
      alert('Please enter a SKU or 1688 URL');
      return;
    }

    setSearching(true);
    if (onSearchStart) {
      onSearchStart();
    }

    try {
      // Extract offerId from URL or use as SKU
      let offerId = trimmed;
      if (trimmed.includes('http') || trimmed.includes('1688') || trimmed.includes('offer')) {
        offerId = extractOfferIdFromUrl(trimmed);
        if (!offerId) {
          alert('Could not extract offer ID from URL');
          if (onSearchComplete) {
            onSearchComplete([]);
          }
          return;
        }
      }

      // Fetch product detail by offerId
      const token = await fetchToken();
      const response = await fetch(`${process.env.REACT_APP_APIUrl1688}/product-detail-by-offerid`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Token': token
        },
        body: JSON.stringify({ offerId: offerId.toString(), country: 'en' })
      });

      if (!response.ok) throw new Error('Failed to fetch product details');

      const data = await response.json();
      const productData = data.result?.result || data.product || data.data || data;

      if (!productData || !productData.offerId) {
        alert('Product not found');
        if (onSearchComplete) {
          onSearchComplete([]);
        }
        return;
      }

      // Process product similar to handleFindSimilar
      const rmbPrice = parseFloat(productData.productSaleInfo?.priceRangeList?.[0]?.price || productData.price || 0);
      const productImage = productData.productImage?.images?.[0] || productData.imageUrl || '';

      // Fetch variant details and get minimum FBP price
      const variantPriceData = await fetchMinVariantPrice(offerId, rmbPrice, productImage);

      // Calculate Importerr price using the ACTIVE pricing algorithm
      let importerrPrice = null;
      if (variantPriceData.minRmb > 0) {
        try {
          const importerrResponse = await fetch(`${BACKEND_API}product/pricing-algorithms/calculate`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              ap: parseFloat(variantPriceData.minRmb),
              ls: 0,
              hsn: false,
              hsnTaxPercent: 18,
              dw: 0.5,
              vw: 0,
              sku: String(offerId || '')
            })
          });
          if (importerrResponse.ok) {
            const importerrResult = await importerrResponse.json();
            importerrPrice = parseFloat(importerrResult?.data?.breakdown?.FBP || importerrResult?.breakdown?.FBP || 0);
          }
        } catch (err) {
          console.error('[SearchBySkuUrl] Error calculating Importerr price:', err);
        }
      }

      const processedProduct = {
        id: offerId || `p-${Date.now()}`,
        offerId,
        title: productData.subjectTrans || productData.subject || productData.title || 'Product',
        image: productImage,
        price: {
          rmb: variantPriceData.minRmb,
          inr: variantPriceData.minInr,
          breakdown: variantPriceData.breakdown
        },
        importerrPrice,
        supplier: productData.companyName || productData.supplier || 'Supplier',
        monthSold: productData.monthSold || 0,
        variantCount: variantPriceData.variantCount,
        moq: productData.moq || productData.minOrderQuantity || 1,
        rating: productData.sellerRating || productData.rating || null,
      };

      if (onSearchComplete) {
        onSearchComplete([processedProduct]);
      }
    } catch (err) {
      console.error('Search by SKU/URL error:', err);
      alert('Search failed: ' + (err.message || 'Unknown error'));
      if (onSearchComplete) {
        onSearchComplete([]);
      }
    } finally {
      setSearching(false);
    }
  }, [searchTerm, extractOfferIdFromUrl, fetchMinVariantPrice, onSearchComplete, onSearchStart]);

  const handleClear = useCallback(() => {
    setSearchTerm('');
    if (onSearchComplete) {
      onSearchComplete([]);
    }
  }, [onSearchComplete]);

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: '300px', maxWidth: '500px' }}>
      <input
        type="text"
        placeholder="Search by SKU or URL..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        onKeyPress={(e) => {
          if (e.key === 'Enter' && !searching && !disabled) {
            handleSearch();
          }
        }}
        style={{
          flex: 1,
          padding: '6px 12px',
          fontSize: '13px',
          border: '1px solid #e2e8f0',
          borderRadius: '4px',
          outline: 'none',
          backgroundColor: '#fff',
          minWidth: '200px'
        }}
        disabled={searching || disabled}
      />
      {searchTerm && (
        <button
          onClick={handleClear}
          style={{
            padding: '4px 8px',
            border: 'none',
            backgroundColor: 'transparent',
            cursor: 'pointer',
            fontSize: '18px',
            color: '#64748b',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            lineHeight: 1,
            width: '24px',
            height: '24px'
          }}
          title="Clear search"
        >
          ×
        </button>
      )}
      <button
        onClick={handleSearch}
        disabled={!searchTerm.trim() || searching || disabled}
        style={{
          padding: '6px 12px',
          fontSize: '13px',
          fontWeight: 500,
          border: 'none',
          borderRadius: '4px',
          backgroundColor: (!searchTerm.trim() || searching || disabled) ? '#e2e8f0' : '#3b82f6',
          color: (!searchTerm.trim() || searching || disabled) ? '#94a3b8' : '#fff',
          cursor: (!searchTerm.trim() || searching || disabled) ? 'not-allowed' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          whiteSpace: 'nowrap'
        }}
        title="Search by SKU or 1688 URL"
      >
        {searching ? (
          <>
            <FaSpinner style={{ animation: 'spin 1s linear infinite' }} />
            Searching...
          </>
        ) : (
          'Search'
        )}
      </button>
    </div>
  );
};

export default SearchBySkuUrl;

